import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'message') setCharCount(value.length);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;
    setStatus('sending');
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      const res = await fetch('https://readdy.ai/api/form/d76i2nsbmgf2o8mm781g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', company: '', phone: '', service: '', message: '' });
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-28 bg-[#F8F5EF]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20">
          {/* Left */}
          <div>
            <p className="text-[#2E5540] text-xs tracking-[0.3em] mb-4 uppercase">/ Contact</p>
            <h2 className="text-4xl md:text-5xl font-bold text-[#1C2B22] tracking-tight leading-tight mb-6">
              まずはお気軽に<br />ご相談ください
            </h2>
            <div className="w-12 h-px bg-[#2E5540] mb-8" />
            <p className="text-[#555] text-base leading-relaxed mb-10">
              初回ご相談は無料です。経営課題・事業の悩みなど、<br className="hidden md:block" />
              どんな小さなことでもお気軽にお問い合わせください。<br className="hidden md:block" />
              オンライン・対面どちらにも対応しています。
            </p>

            <div className="space-y-6">
              {[
                {
                  icon: 'ri-user-line',
                  label: '代表',
                  value: '後藤 慎吾',
                },
                {
                  icon: 'ri-map-pin-line',
                  label: '所在地',
                  value: '大阪府大阪市福島区福島',
                },
                { icon: 'ri-mail-line', label: 'メール', value: 'goto_sn@grain-n.com' },
                { icon: 'ri-smartphone-line', label: 'モバイル', value: '080-5353-0610' },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#1C2B22] shrink-0">
                    <i className={`${item.icon} text-lg text-white`} />
                  </div>
                  <div>
                    <p className="text-xs text-[#888] mb-1 tracking-widest">{item.label}</p>
                    <p className="text-sm text-[#333] whitespace-pre-line leading-relaxed">
                      {item.value}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-white rounded-2xl p-8 md:p-10">
            <h3 className="text-xl font-bold text-[#1C2B22] mb-7">お問い合わせフォーム</h3>

            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#E8F0EB] mb-4">
                  <i className="ri-check-line text-3xl text-[#2E5540]" />
                </div>
                <h4 className="text-lg font-bold text-[#1C2B22] mb-2">送信が完了しました</h4>
                <p className="text-sm text-[#666] leading-relaxed">
                  お問い合わせありがとうございます。<br />
                  3営業日以内にご返信いたします。
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-6 text-sm text-[#2E5540] underline cursor-pointer"
                >
                  フォームに戻る
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs text-[#666] mb-1.5 tracking-wide">
                      お名前 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="後藤 慎吾"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#333] focus:outline-none focus:border-[#2E5540] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[#666] mb-1.5 tracking-wide">
                      会社名
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="株式会社〇〇"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#333] focus:outline-none focus:border-[#2E5540] transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-[#666] mb-1.5 tracking-wide">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="example@company.com"
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#333] focus:outline-none focus:border-[#2E5540] transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs text-[#666] mb-1.5 tracking-wide">
                    電話番号
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="080-0000-0000"
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#333] focus:outline-none focus:border-[#2E5540] transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs text-[#666] mb-1.5 tracking-wide">
                    ご相談内容
                  </label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#333] focus:outline-none focus:border-[#2E5540] transition-colors cursor-pointer bg-white"
                  >
                    <option value="">選択してください</option>
                    <option value="WEBサイト制作">WEBサイト制作</option>
                    <option value="MEO対策（Googleマップ集客）">MEO対策（Googleマップ集客）</option>
                    <option value="SNS運用・Instagram支援">SNS運用・Instagram支援</option>
                    <option value="ホットペッパー最適化">ホットペッパー最適化</option>
                    <option value="WEBサイト保守・管理">WEBサイト保守・管理</option>
                    <option value="デジタルマーケティング全般">デジタルマーケティング全般</option>
                    <option value="その他">その他</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-[#666] mb-1.5 tracking-wide">
                    メッセージ <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="お気軽にご相談の内容をお書きください"
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#333] focus:outline-none focus:border-[#2E5540] transition-colors resize-none"
                  />
                  <p className={`text-xs text-right mt-1 ${charCount > 500 ? 'text-red-500' : 'text-[#999]'}`}>
                    {charCount} / 500文字
                  </p>
                </div>

                {status === 'error' && (
                  <p className="text-sm text-red-500">
                    送信に失敗しました。もう一度お試しください。
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'sending' || charCount > 500}
                  className="w-full bg-[#1C2B22] text-white py-4 rounded-full text-sm tracking-widest hover:bg-[#2E5540] transition-colors duration-300 cursor-pointer disabled:opacity-60 whitespace-nowrap"
                >
                  {status === 'sending' ? '送信中...' : '送信する'}
                </button>

                <p className="text-xs text-[#999] text-center">
                  ご入力いただいた情報は、お問い合わせ対応のみに使用します。
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
